import { PERCENT_SCALE, SECONDS_PER_MINUTE } from 'utils/units'
import { resolveVideoUrls } from 'utils/videoUrlResolver'

import type { components } from '@startech/openapi'
import type { AgentMessage } from 'modules/coaching/types'

export type ConversationWithFile = components['schemas']['ConversationWithFile']
export type ConversationFile = NonNullable<ConversationWithFile['file']>
type ResolvedVideoUrls = NonNullable<ReturnType<typeof resolveVideoUrls>>

export type UploadedVideoSource = {
  fileId: string
  isPrimary: boolean
  label: string
  mux?: ResolvedVideoUrls['mux']
  playbackUrl?: string
  title?: string | null
  videoDuration?: number
  videoUrl: string
}

type ConversationPlaybackState = {
  muxPlayback: ResolvedVideoUrls['mux'] | undefined
  videoDuration: number | undefined
  videoUrl: string
}

type ProcessingViewState = {
  processingPanelPercentage: number
  processingPanelStatusText: string
  shouldRenderProcessingView: boolean
}

const MAXIMUM_DOWNLOAD_TITLE_LENGTH = 120

export const getInputPlaceholder = (isFileFailed: boolean, isFileProcessing: boolean): string => {
  if (isFileProcessing) {
    return 'Video is being processed...'
  }
  if (isFileFailed) {
    return 'Video processing failed'
  }
  return 'Ask about your technique...'
}

export const timeToSeconds = (time: string): number => {
  const parts = time.split(':').map(Number)
  return parts[0] * SECONDS_PER_MINUTE + parts[1]
}

const getProcessingPanelStatusText = (isReadyForFirstQuestion: boolean): string => {
  if (isReadyForFirstQuestion) {
    return 'Ready'
  }

  return 'Uploading...'
}

const getVideoFileLabel = (parameters: { index: number; isPrimary: boolean; title?: string | null }): string => {
  const title = parameters.title?.trim()
  if (title) {
    return title
  }

  return parameters.isPrimary ? 'Original video' : `Added video ${parameters.index}`
}

export const isConversationFileProcessing = (conversationFile: ConversationFile | undefined): boolean => {
  return (
    Boolean(conversationFile) &&
    (conversationFile?.processing_status === 'pending' || conversationFile?.processing_status === 'processing')
  )
}

export const isConversationFileFailed = (conversationFile: ConversationFile | undefined): boolean => {
  return conversationFile?.processing_status === 'failed'
}

export const getPersistedVideoArtifactUrl = (messages: AgentMessage[]): string | undefined => {
  for (const message of messages) {
    for (const content of message.contents) {
      if (content.type === 'video_artifact') {
        return content.videoUrl
      }
    }
  }
}

export const toUploadedVideoSource = (
  file: ConversationFile,
  index: number,
  conversationFileId: string | null | undefined
): UploadedVideoSource[] => {
  const fallbackUrl = file.playback_url ?? undefined
  const resolved = resolveVideoUrls(file, fallbackUrl)
  if (!resolved?.streamUrl) {
    return []
  }

  const isPrimary = file.id === conversationFileId
  return [
    {
      fileId: file.id,
      isPrimary,
      label: getVideoFileLabel({ index, isPrimary, title: file.title }),
      mux: resolved.mux,
      playbackUrl: file.playback_url ?? undefined,
      title: file.title,
      videoDuration: file.duration ?? undefined,
      videoUrl: resolved.streamUrl,
    },
  ]
}

export const hasNonVideoArtifactContent = (message: AgentMessage): boolean => {
  return message.contents.some(content => content.type !== 'video_artifact')
}

const hasVideoArtifactContent = (message: AgentMessage): boolean => {
  return message.contents.some(content => content.type === 'video_artifact')
}

const isVideoArtifactOnlyMessage = (message: AgentMessage): boolean => {
  return message.contents.length > 0 && message.contents.every(content => content.type === 'video_artifact')
}

const normalizePlaybackUrl = (url: string): string => {
  return url.split('?')[0] ?? url
}

const collectPersistedArtifactUrls = (messages: AgentMessage[]): Set<string> => {
  const urls = new Set<string>()
  for (const message of messages) {
    for (const content of message.contents) {
      if (content.type === 'video_artifact' && content.videoUrl) {
        urls.add(normalizePlaybackUrl(content.videoUrl))
      }
    }
  }
  return urls
}

const buildArtifactMessage = (parameters: { fileId: string; videoUrl: string; timestamp?: Date }): AgentMessage => {
  return {
    id: `video_artifact:${parameters.fileId}`,
    role: 'user',
    contents: [{ type: 'video_artifact', videoUrl: parameters.videoUrl }],
    timestamp: parameters.timestamp ?? new Date(),
  }
}

/**
 * Keep every uploaded-video artifact above the first real chat turn, and synthesize
 * missing artifacts for ready conversation files so follow-up uploads always render.
 */
export const getDisplayMessages = (parameters: {
  conversationFileId: string | null | undefined
  conversationId: string | undefined
  messages: AgentMessage[]
  videoSources?: UploadedVideoSource[]
  videoUrl: string
}): AgentMessage[] => {
  const artifactOnlyMessages = parameters.messages.filter(message => isVideoArtifactOnlyMessage(message))
  const otherMessages = parameters.messages.filter(message => !isVideoArtifactOnlyMessage(message))
  const persistedArtifactUrls = collectPersistedArtifactUrls(parameters.messages)

  const synthesizedFromSources = (parameters.videoSources ?? [])
    .filter(source => {
      const candidates = [source.videoUrl, source.playbackUrl].flatMap(url => (url ? [url] : []))
      return !candidates.some(url => persistedArtifactUrls.has(normalizePlaybackUrl(url)))
    })
    .map(source => buildArtifactMessage({ fileId: source.fileId, videoUrl: source.videoUrl }))

  const synthesizedFromPrimary =
    synthesizedFromSources.length === 0 && artifactOnlyMessages.length === 0 && parameters.videoUrl
      ? [
          buildArtifactMessage({
            fileId: parameters.conversationFileId ?? parameters.conversationId ?? 'unknown',
            videoUrl: parameters.videoUrl,
          }),
        ]
      : []

  const leadingArtifacts = [...artifactOnlyMessages, ...synthesizedFromSources, ...synthesizedFromPrimary]

  if (leadingArtifacts.length === 0) {
    return parameters.messages
  }

  return [...leadingArtifacts, ...otherMessages]
}

export const getDownloadFileName = (title: string | null | undefined): string => {
  const rawTitle = title?.trim()
  if (!rawTitle) {
    return 'video.mp4'
  }

  const safe = rawTitle.replace(/[/\\?%*:|"<>]/g, '-').slice(0, MAXIMUM_DOWNLOAD_TITLE_LENGTH)
  return safe.endsWith('.mp4') ? safe : `${safe}.mp4`
}

export const getChatCompleteHandler = (
  conversationId: string | undefined,
  invalidateConversationMessages: (conversationId: string) => void
): (() => void) | undefined => {
  if (!conversationId) {
    return undefined
  }

  return () => invalidateConversationMessages(conversationId)
}

export const getConversationPlaybackState = (parameters: {
  backgroundVideoUrl: string | undefined
  conversationFile: ConversationFile | undefined
  persistedVideoArtifactUrl: string | undefined
}): ConversationPlaybackState => {
  const fallbackVideoUrl =
    parameters.backgroundVideoUrl ?? parameters.conversationFile?.playback_url ?? parameters.persistedVideoArtifactUrl
  const resolvedUrls = parameters.conversationFile
    ? resolveVideoUrls(parameters.conversationFile, fallbackVideoUrl)
    : null

  return {
    muxPlayback: resolvedUrls?.mux,
    videoDuration: parameters.conversationFile?.duration ?? undefined,
    videoUrl: resolvedUrls?.streamUrl ?? '',
  }
}

export const getIsProcessingActive = (parameters: {
  hasPendingUploadForCurrentConversation: boolean
  isFileProcessing: boolean
  uploading: boolean
}): boolean => {
  return parameters.hasPendingUploadForCurrentConversation || parameters.uploading || parameters.isFileProcessing
}

export const getInputDisabled = (isProcessingActive: boolean, isFileFailed: boolean): boolean => {
  return isProcessingActive || isFileFailed
}

export const getIsFollowUpUploadInProgress = (
  isFollowUpUploadPending: boolean,
  followUpUploadProgress: { percentage: number; phase: 'uploading' } | undefined
): boolean => {
  return isFollowUpUploadPending || Boolean(followUpUploadProgress)
}

const getShouldKeepFirstPromptView = (parameters: {
  hasConversationFile: boolean
  hasSentFirstQuestion: boolean
  isFileFailed: boolean
  isMessagesLoading: boolean
  isProcessingActive: boolean
}): boolean => {
  return (
    !parameters.isProcessingActive &&
    !parameters.isFileFailed &&
    !parameters.isMessagesLoading &&
    parameters.hasConversationFile &&
    !parameters.hasSentFirstQuestion
  )
}

export const getProcessingState = (parameters: {
  hasConversationFile: boolean
  hasSentFirstQuestion: boolean
  isFileFailed: boolean
  isMessagesLoading: boolean
  isProcessingActive: boolean
  uploadProgress: { percentage: number } | undefined
}): ProcessingViewState => {
  const shouldKeepFirstPromptView = getShouldKeepFirstPromptView(parameters)
  const shouldRenderProcessingView = parameters.isProcessingActive || shouldKeepFirstPromptView

  return {
    processingPanelPercentage: shouldKeepFirstPromptView ? PERCENT_SCALE : (parameters.uploadProgress?.percentage ?? 0),
    processingPanelStatusText: getProcessingPanelStatusText(shouldKeepFirstPromptView),
    shouldRenderProcessingView,
  }
}

export const getInitialUploadMessageIndex = (displayMessages: AgentMessage[]): number => {
  return displayMessages.findIndex(message => hasVideoArtifactContent(message))
}

export const getBackgroundVideoUrls = (parameters: {
  pendingPreviewUrls: string[]
  videoSources: UploadedVideoSource[]
  fallbackVideoUrl: string
}): string[] => {
  if (parameters.pendingPreviewUrls.length > 0) {
    return parameters.pendingPreviewUrls
  }

  const readyUrls = parameters.videoSources.map(source => source.videoUrl).filter(Boolean)
  if (readyUrls.length > 0) {
    return readyUrls
  }

  return parameters.fallbackVideoUrl ? [parameters.fallbackVideoUrl] : []
}

export const getDownloadVideoUrl = (conversationFile: ConversationFile | undefined): string | undefined => {
  return conversationFile?.playback_url ?? undefined
}

export const getIsFollowUpUploadDisabled = (inputDisabled: boolean, isFollowUpUploadInProgress: boolean): boolean => {
  return inputDisabled || isFollowUpUploadInProgress
}

export const getIsUploadLeaveProtectionEnabled = (uploading: boolean, isFollowUpUploadPending: boolean): boolean => {
  return uploading || isFollowUpUploadPending
}

export const getShouldRenderUploadFailure = (parameters: {
  hasPendingUploadForCurrentConversation: boolean
  uploadError: string | undefined
  uploading: boolean
}): boolean => {
  return parameters.hasPendingUploadForCurrentConversation && Boolean(parameters.uploadError) && !parameters.uploading
}

export const getShouldRenderConversationNotFound = (parameters: {
  conversation: ConversationWithFile | undefined
  conversationError: unknown
  isConversationLoading: boolean
}): boolean => {
  return Boolean(parameters.conversationError) || (!parameters.isConversationLoading && !parameters.conversation)
}

export const getShouldRenderChatLoading = (parameters: {
  authLoading: boolean
  isConversationLoading: boolean
  isMessagesLoading: boolean
  videoUrl: string
}): boolean => {
  return (
    parameters.authLoading || parameters.isConversationLoading || parameters.isMessagesLoading || !parameters.videoUrl
  )
}
